// Items sold by weight on the till (#917, UC-3.R9-R10): how the screen names a
// weighed portion and shows a kilo price, and how it asks its order store for
// them.
//
// The store leaves weighed items out of the menu, and gives a weighed line
// under its compat name, to a screen that does not ask (decision
// 2026-10-27-sell-items-by-weight). This screen reads them whole, so every
// menu and orders read it makes asks with weighed=1. It also prices per-kg
// choices (#935, UC-3.R11), so it asks perKg=1: a store leaves every item
// with a per-kg group out for a screen that does not ask.

import { formatMinor } from '../format/money';
import { fill, message, type Language } from '../i18n/messages';

/**
 * The query a menu or orders read sends, so the store answers weighed items
 * whole and their per-kg groups with them.
 */
export const askWeighed = (): string => 'weighed=1&perKg=1';

/** True for an item the menu sells by weight. */
export function soldByWeight(item: { soldBy?: string | null }): boolean {
	return item.soldBy === 'weight';
}

/** A line's name with its grams, "سمك · 250 جم", or the name alone for a piece line. */
export function lineName(language: Language, name: string, grams?: number | null): string {
	if (!grams) return name;
	return `${name} · ${grams} ${message(language, 'unitGrams')}`;
}

/** A per-kg choice's rate as it reads: "+50.00 / كجم", or "−20.00 / كجم". */
export function perKgRate(language: Language, perKgMinor: number, minorDigits: number): string {
	const price = formatMinor(perKgMinor, minorDigits);
	return fill(message(language, 'perKilo'), perKgMinor > 0 ? `+${price}` : price);
}

/** A tile's price: a kilo price reads "400.00 / كجم", a piece price as it is. */
export function tilePrice(
	language: Language,
	item: { priceMinor: number; soldBy?: string | null },
	minorDigits: number,
): string {
	const price = formatMinor(item.priceMinor, minorDigits);
	return soldByWeight(item) ? fill(message(language, 'perKilo'), price) : price;
}
